import React, { useState, useEffect } from "react";
import {
  useGetDayWisePobDataMutation,
  useGetHierarchyStructureQuery,
} from "state/api";
import { useTheme } from "@mui/material/styles";
import { RingLoader } from "react-spinners";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DownloadOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
} from "@mui/material";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import FlexBetween from "components/FlexBetween";
import * as XLSX from "xlsx";

const getMonthYearLabel = (month, year) => {
  const date = new Date(year, month - 1);
  const monthName = date.toLocaleString("default", { month: "long" });
  return `${monthName} ${year}`;
};

const getFinancialYearLabel = (month, year) => {
  const fyStartYear = month <= 3 ? year - 1 : year;
  const fyEndYear = fyStartYear + 1;
  return `${fyStartYear}-${fyEndYear.toString().slice(-2)}`;
};

const getMonthlyYearWiseData = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const current = {
    month: currentMonth,
    year: currentYear,
    offset: 0,
    label: `${getMonthYearLabel(
      currentMonth,
      currentYear
    )} – FY ${getFinancialYearLabel(currentMonth, currentYear)}`,
  };

  let lastMonth = currentMonth - 1;
  let lastMonthYear = currentYear;
  if (lastMonth === 0) {
    lastMonth = 12;
    lastMonthYear -= 1;
  }

  const last = {
    month: lastMonth,
    year: lastMonthYear,
    offset: -1,
    label: `${getMonthYearLabel(
      lastMonth,
      lastMonthYear
    )} – FY ${getFinancialYearLabel(lastMonth, lastMonthYear)}`,
  };

  return [current, last];
};

const DailyWisePob = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthYearLabel, setMonthYearLabel] = useState("");
  const [getDayWiseData] = useGetDayWisePobDataMutation();
  const { data: hierarchyStructureData } = useGetHierarchyStructureQuery();
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [hierarchicalData, setHierarchicalData] = useState([]);
  const [openSections, setOpenSections] = useState({});

  // Updated filters without division
  const initialFilters = {
    rm: "",
    am: "",
    mr: "",
    pobType: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState("hierarchy");

  const handleFetch = async (month, year, label) => {
    console.log("Fetching data for:", month, year);
    setLoading(true);

    try {
      const response = await getDayWiseData({ month, year });
      if (response?.data?.success) {
        // Set data even if it's empty - we'll show hierarchy with 0 values
        setData(response.data.data || []);
        setMonthYearLabel(label);
        setError(null);
      } else {
        // Set empty data array instead of error - still show hierarchy with 0s
        setData([]);
        setMonthYearLabel(label);
        setError(null);
      }
    } catch (error) {
      console.error("Error fetching day-wise data:", error);
      // Show error only for actual connection/server issues
      setError("An error occurred while fetching the data.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const [current] = getMonthlyYearWiseData();
    handleFetch(current.month, current.year, current.label);
  }, []);

  const monthlyOptions = getMonthlyYearWiseData();

  useEffect(() => {
    if (data && data.length >= 0) {
      const transformedData = (rawData) => {
        const hierarchy = {};
        const fields = Array.from({ length: 31 }, (_, i) => `Day_${i + 1}`);

        // Initialize hierarchy structure with 0 values from hierarchy structure data
        if (
          hierarchyStructureData?.data &&
          hierarchyStructureData.data.length > 0
        ) {
          hierarchyStructureData.data.forEach((row) => {
            if (!hierarchy[row.RmMgName]) {
              hierarchy[row.RmMgName] = {
                RmMgName: row.RmMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const rm = hierarchy[row.RmMgName];

            if (!rm.children[row.AmMgName]) {
              rm.children[row.AmMgName] = {
                AmMgName: row.AmMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const am = rm.children[row.AmMgName];

            if (!am.children[row.MrMgName]) {
              am.children[row.MrMgName] = {
                MrMgName: row.MrMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const mrMg = am.children[row.MrMgName];

            if (!mrMg.children[row.MrName]) {
              mrMg.children[row.MrName] = {
                MrName: row.MrName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }
          });
        }

        // If there's actual POB data, merge it into the hierarchy
        if (rawData && rawData.length > 0) {
          rawData.forEach((row) => {
            const parsedRow = {};
            fields.forEach((field) => {
              parsedRow[field] = parseInt(row[field]) || 0;
            });

            // Ensure RM exists
            if (!hierarchy[row.RmMgName]) {
              hierarchy[row.RmMgName] = {
                RmMgName: row.RmMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const rm = hierarchy[row.RmMgName];
            fields.forEach((field) => (rm[field] += parsedRow[field]));

            if (!rm.children[row.AmMgName]) {
              rm.children[row.AmMgName] = {
                AmMgName: row.AmMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const am = rm.children[row.AmMgName];
            fields.forEach((field) => (am[field] += parsedRow[field]));

            if (!am.children[row.MrMgName]) {
              am.children[row.MrMgName] = {
                MrMgName: row.MrMgName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const mrMg = am.children[row.MrMgName];
            fields.forEach((field) => (mrMg[field] += parsedRow[field]));

            if (!mrMg.children[row.MrName]) {
              mrMg.children[row.MrName] = {
                MrName: row.MrName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const mr = mrMg.children[row.MrName];
            fields.forEach((field) => (mr[field] += parsedRow[field]));

            if (!mr.children[row.DoctName]) {
              mr.children[row.DoctName] = {
                DoctName: row.DoctName,
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
                children: {},
              };
            }

            const doct = mr.children[row.DoctName];
            fields.forEach((field) => (doct[field] += parsedRow[field]));

            if (!doct.children) doct.children = {};

            // Create key that includes pobType
            const chemistKey = `${row.Chemist_name} (${
              row.pobType || "Unknown"
            })`;

            if (!doct.children[chemistKey]) {
              doct.children[chemistKey] = {
                Chemist_name: row.Chemist_name,
                pobType: row.pobType || "Unknown",
                ...fields.reduce((acc, field) => ({ ...acc, [field]: 0 }), {}),
              };
            }

            const chemist = doct.children[chemistKey];
            fields.forEach((field) => (chemist[field] += parsedRow[field]));
          });
        }

        return Object.values(hierarchy).map((rm) => {
          rm.children = Object.values(rm.children).map((am) => {
            am.children = Object.values(am.children).map((mrMg) => {
              mrMg.children = Object.values(mrMg.children).map((mr) => {
                mr.children = Object.values(mr.children).map((doct) => {
                  doct.children = Object.values(doct.children).map(
                    (chemist) => ({
                      Chemist_name: chemist.Chemist_name,
                      pobType: chemist.pobType || "Unknown",
                      ...fields.reduce(
                        (acc, field) => ({ ...acc, [field]: chemist[field] }),
                        {}
                      ),
                    })
                  );

                  return {
                    DoctName: doct.DoctName,
                    ...fields.reduce(
                      (acc, field) => ({ ...acc, [field]: doct[field] }),
                      {}
                    ),
                    children: doct.children,
                  };
                });

                return {
                  MrName: mr.MrName,
                  ...fields.reduce(
                    (acc, field) => ({ ...acc, [field]: mr[field] }),
                    {}
                  ),
                  children: mr.children,
                };
              });

              return {
                MrMgName: mrMg.MrMgName,
                ...fields.reduce(
                  (acc, field) => ({ ...acc, [field]: mrMg[field] }),
                  {}
                ),
                children: mrMg.children,
              };
            });

            return {
              AmMgName: am.AmMgName,
              ...fields.reduce(
                (acc, field) => ({ ...acc, [field]: am[field] }),
                {}
              ),
              children: am.children,
            };
          });

          return {
            RmMgName: rm.RmMgName,
            ...fields.reduce(
              (acc, field) => ({ ...acc, [field]: rm[field] }),
              {}
            ),
            children: rm.children,
          };
        });
      };

      setHierarchicalData(transformedData(data));
    }
  }, [data, hierarchyStructureData]);

  useEffect(() => {
    if (hierarchicalData.length > 0) {
      const filtered = hierarchicalData
        .filter((rm) => !filters.rm || rm.RmMgName === filters.rm)
        .map((rm) => {
          const amChildren = rm.children
            .filter((am) => !filters.am || am.AmMgName === filters.am)
            .map((am) => {
              const mrMgChildren = am.children
                .filter((mrMg) => !filters.mr || mrMg.MrMgName === filters.mr)
                .map((mrMg) => {
                  const mrChildren = mrMg.children.map((mr) => {
                    const doctChildren = mr.children.map((doct) => {
                      const chemistChildren = doct.children
                        .filter(
                          (chemist) =>
                            !filters.pobType ||
                            chemist.pobType === filters.pobType
                        )
                        .map((chemist) => ({
                          ...chemist,
                        }));
                      return { ...doct, children: chemistChildren };
                    });
                    return { ...mr, children: doctChildren };
                  });
                  return { ...mrMg, children: mrChildren };
                });
              return { ...am, children: mrMgChildren };
            });
          return { ...rm, children: amChildren };
        })
        .filter((rm) => rm.children.length > 0);

      setFilteredData(filtered);
    }
  }, [hierarchicalData, filters]);

  const toggleSection = (id) => {
    setOpenSections((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatValue = (value) => {
    return Number.isInteger(value) ? value : "-";
  };

  const renderRows = (data, level = 0) => {
    // Color scheme matching DailyWise component
    const colorScheme = {
      0: { bg: "rgba(59, 130, 246, 0.1)", text: "#3b82f6", border: "#3b82f6" }, // RM - Blue
      1: { bg: "rgba(59, 130, 246, 0.1)", text: "#3b82f6", border: "#3b82f6" }, // AM - Blue
      2: { bg: "rgba(168, 85, 247, 0.08)", text: "#a855f7", border: "#a855f7" }, // MR Manager - Purple
      3: { bg: "rgba(168, 85, 247, 0.08)", text: "#a855f7", border: "#a855f7" }, // MR - Purple
      4: { bg: "rgba(16, 185, 129, 0.05)", text: "#10b981", border: "#10b981" }, // Doctor - Green
      5: { bg: "rgba(16, 185, 129, 0.02)", text: "#10b981", border: "#10b981" }, // Chemist - Light Green
    };

    return data.map((item, index) => {
      const id = `${level}-${index}`;
      const isOpen = openSections[id];
      const colorConfig = colorScheme[level] || {
        bg: "transparent",
        text: theme.palette.text.primary,
        border: "#ccc",
      };

      // Mobile view
      if (isMobile) {
        return (
          <div
            key={id}
            style={{
              marginBottom: "10px",
              border: `1px solid ${colorConfig.border}`,
              borderRadius: "8px",
              padding: "10px",
              backgroundColor: colorConfig.bg,
              cursor: "pointer",
            }}
          >
            <div
              onClick={() => toggleSection(id)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: "bold",
                color: colorConfig.text,
              }}
            >
              <span>
                {level === 0
                  ? item.RmMgName
                  : level === 1
                  ? item.AmMgName
                  : level === 2
                  ? item.MrMgName
                  : level === 3
                  ? item.MrName
                  : level === 4
                  ? item.DoctName
                  : `${item.Chemist_name} (${item.pobType || "Unknown"})`}
              </span>
              {item.children && <span>{isOpen ? "▼" : "►"}</span>}
            </div>
            <div style={{ marginTop: "10px" }}>
              {Object.entries(item)
                .filter(([key]) => key !== "children" && key.startsWith("Day_"))
                .map(([key, value]) => (
                  <div key={key}>
                    <strong>{key}:</strong> {formatValue(value)}
                  </div>
                ))}
            </div>
            {isOpen && item.children && (
              <div style={{ marginLeft: "10px" }}>
                {renderRows(item.children, level + 1)}
              </div>
            )}
          </div>
        );
      }

      // Desktop view
      return (
        <React.Fragment key={id}>
          <TableRow
            onClick={() => toggleSection(id)}
            sx={{
              cursor: item.children ? "pointer" : "default",
              backgroundColor: colorConfig.bg,
              borderLeft: `4px solid ${colorConfig.border}`,
              "&:hover": {
                backgroundColor: colorConfig.bg,
              },
            }}
          >
            <TableCell
              sx={{
                paddingLeft: `${level * 20}px`,
                color: colorConfig.text,
                fontWeight: "bold",
              }}
            >
              {item.children && (
                <span style={{ marginRight: "5px" }}>{isOpen ? "▼" : "►"}</span>
              )}
              {level === 0
                ? item.RmMgName
                : level === 1
                ? item.AmMgName
                : level === 2
                ? item.MrMgName
                : level === 3
                ? item.MrName
                : level === 4
                ? item.DoctName
                : `${item.Chemist_name} (${item.pobType || "Unknown"})`}
            </TableCell>
            {Array.from({ length: 31 }, (_, i) => (
              <TableCell key={i} align="right" sx={{ color: colorConfig.text }}>
                {formatValue(item[`Day_${i + 1}`])}
              </TableCell>
            ))}
          </TableRow>
          {isOpen && item.children && renderRows(item.children, level + 1)}
        </React.Fragment>
      );
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredData.flatMap((rm) =>
        rm.children.flatMap((am) =>
          am.children.flatMap((mrmg) =>
            mrmg.children.flatMap((mr) =>
              mr.children.flatMap((doctor) =>
                doctor.children.map((chemist) => ({
                  RmMgName: rm.RmMgName,
                  AmMgName: am.AmMgName,
                  MrMgName: mrmg.MrMgName,
                  MrName: mr.MrName,
                  DoctName: doctor.DoctName,
                  Chemist_name: chemist.Chemist_name,
                  pobType: chemist.pobType || "Unknown",
                  ...Object.fromEntries(
                    Array.from({ length: 31 }, (_, i) => [
                      `Day_${i + 1}`,
                      chemist[`Day_${i + 1}`],
                    ])
                  ),
                }))
              )
            )
          )
        )
      )
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "POB Data");
    XLSX.writeFile(workbook, "pob_data.xlsx");
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#0f172a" }}
      >
        <RingLoader color="#3b82f6" size={100} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, backgroundColor: "#0f172a", minHeight: "100vh" }}>
        <Card
          sx={{
            backgroundColor: "#7f1d1d",
            borderLeft: "4px solid #ef4444",
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Typography sx={{ color: "#fca5a5" }}>❌ {error}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: "#0f172a", minHeight: "100vh" }}>
      {/* Header Card */}
      <Card
        sx={{
          mb: 4,
          background: "linear-gradient(135deg, #3b82f6 0%, #ef4444 100%)",
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(59, 130, 246, 0.3)",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  mb: 0.5,
                }}
              >
                📋 Daily Wise POB Report
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.8)" }}
              >
                Chemist-wise POB tracking across all days
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosNew />}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Back
              </Button>
              <Select
                value={monthYearLabel}
                onChange={(e) => {
                  const selected = monthlyOptions.find(
                    (opt) => opt.label === e.target.value
                  );
                  if (selected)
                    handleFetch(selected.month, selected.year, selected.label);
                }}
                sx={{
                  minWidth: 250,
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                {monthlyOptions.map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "#3b82f6",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.3s ease",
                }}
                startIcon={<RefreshIcon />}
                onClick={() => {
                  const [current] = getMonthlyYearWiseData();
                  handleFetch(current.month, current.year, current.label);
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#10b981",
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#059669",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
                startIcon={<DownloadOutlined />}
                onClick={downloadExcel}
              >
                Export
              </Button>
              <Button
                variant="contained"
                onClick={() => setFilterDialogOpen(true)}
                sx={{
                  backgroundColor: "#a855f7",
                  color: "white",
                  fontWeight: "bold",
                  "&:hover": {
                    backgroundColor: "#9333ea",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(168, 85, 247, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Filters
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Table Section */}
      <Card
        sx={{
          backgroundColor: "#1e293b",
          borderRadius: 2,
          borderTop: "2px solid #3b82f6",
        }}
      >
        <CardContent>
          {filteredData.length > 0 ? (
            <TableContainer
              sx={{
                boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
                borderRadius: 2,
                overflow: "auto",
                border: "1px solid rgba(59, 130, 246, 0.2)",
                backgroundColor: "#1e293b",
                maxHeight: "calc(100vh - 400px)",
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "white",
                        fontSize: "0.85rem",
                        padding: "8px 6px",
                      }}
                    >
                      Hierarchy
                    </TableCell>
                    {Array.from({ length: 31 }, (_, i) => (
                      <TableCell
                        key={i}
                        align="right"
                        sx={{
                          fontWeight: "bold",
                          color: "white",
                          backgroundColor: "#2563eb",
                          fontSize: "0.75rem",
                          padding: "6px 2px",
                          width: 38,
                          minWidth: 38,
                        }}
                      >
                        {i + 1}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{renderRows(filteredData)}</TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="h6"
              textAlign="center"
              p={3}
              sx={{ color: "#94a3b8" }}
            >
              No data available
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Filter Dialog */}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1e293b",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "white",
            backgroundColor: "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
            backgroundImage: "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
          }}
        >
          Apply Filters
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#1e293b" }}>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            minWidth={300}
            mt={1}
          >
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>RM</InputLabel>
              <Select
                name="rm"
                value={filters.rm}
                onChange={handleFilterChange}
                label="RM"
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <MenuItem value="">All RM</MenuItem>
                {Array.from(
                  new Set(hierarchicalData.map((item) => item.RmMgName))
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>AM</InputLabel>
              <Select
                name="am"
                value={filters.am}
                onChange={handleFilterChange}
                label="AM"
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <MenuItem value="">All AM</MenuItem>
                {Array.from(
                  new Set(
                    hierarchicalData.flatMap((rm) =>
                      rm.children.map((am) => am.AmMgName)
                    )
                  )
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>MR</InputLabel>
              <Select
                name="mr"
                value={filters.mr}
                onChange={handleFilterChange}
                label="MR"
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <MenuItem value="">All MR</MenuItem>
                {Array.from(
                  new Set(
                    hierarchicalData.flatMap((rm) =>
                      rm.children.flatMap((am) =>
                        am.children.map((mrmg) => mrmg.MrMgName)
                      )
                    )
                  )
                ).map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "white" }}>POB Type</InputLabel>
              <Select
                name="posebType"
                value={filters.pobType}
                onChange={handleFilterChange}
                label="POB Type"
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.3)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255,255,255,0.5)",
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <MenuItem value="">All POB Types</MenuItem>
                {Array.from(
                  new Set(
                    hierarchicalData.flatMap((rm) =>
                      rm.children.flatMap((am) =>
                        am.children.flatMap((mrmg) =>
                          mrmg.children.flatMap((mr) =>
                            mr.children.flatMap((doctor) =>
                              doctor.children.map(
                                (chemist) => chemist.posebType || "Unknown"
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                ).map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "#1e293b",
            borderTop: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Button onClick={clearFilters} sx={{ color: "#f59e0b" }}>
            Clear All
          </Button>
          <Button
            onClick={() => setFilterDialogOpen(false)}
            sx={{
              backgroundColor: "#3b82f6",
              color: "white",
              "&:hover": { backgroundColor: "#2563eb" },
            }}
            variant="contained"
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyWisePob;
